import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import React from "react";
import {apiActionImproved, apiRetrieveImproved} from "../../../api";
import PButton from "../../../components/PButton.tsx";
import Header from "../../../Header.tsx";

export const Route = createFileRoute('/_app/master/club-listings')({
    component: RouteComponent,
})

type ClubListing = {
    uid: string; name: string; sport_types?: string; description?: string;
    image_url?: string; logo_url?: string; address?: string; city?: string; country?: string;
    lat?: number; lng?: number; phone?: string; email?: string; website?: string;
    contact_name?: string; contact_email?: string; contact_phone?: string;
    status: string; created_at?: string;
};

function statusChip(s: string) {
    if (s === 'approved') return <Chip color="success" size="sm" variant="flat">Approved</Chip>;
    if (s === 'rejected') return <Chip color="danger" size="sm" variant="flat">Rejected</Chip>;
    return <Chip color="warning" size="sm" variant="flat">Pending</Chip>;
}

function RouteComponent() {
    const queryClient = useQueryClient();

    const query = useQuery<{clubs: ClubListing[]}>({
        queryKey: ['master-club-listings'],
        queryFn:  () => apiRetrieveImproved('master/club-listings' as any),
    });

    const setStatus = useMutation({
        mutationFn: async ({uid, status}: {uid: string; status: string}) =>
            apiActionImproved(`master/club-listings/${uid}/status` as any, {status}, {method: 'POST'}),
        onMutate:   async ({uid, status}) => {
            await queryClient.cancelQueries({queryKey: ['master-club-listings']});
            const prev = queryClient.getQueryData<{clubs: ClubListing[]}>(['master-club-listings']);
            queryClient.setQueryData<{clubs: ClubListing[]}>(['master-club-listings'], (old) =>
                old ? {...old, clubs: old.clubs.map((c) => c.uid === uid ? {...c, status} : c)} : old);
            return {prev};
        },
        onError:    (_e, _v, ctx) => {
            if (ctx?.prev) queryClient.setQueryData(['master-club-listings'], ctx.prev);
            addToast({title: 'Could not update status', color: 'danger'});
        },
        onSuccess:  (_d, {status}) => addToast({
            title: status === 'approved' ? 'Club approved' : status === 'rejected' ? 'Club rejected' : 'Club updated',
            color: status === 'rejected' ? 'warning' : 'success',
        }),
        onSettled:  () => queryClient.invalidateQueries({queryKey: ['master-club-listings']}),
    });

    const clubs = query.data?.clubs || [];

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-5xl py-4">
                <h1 className="mb-1 text-2xl font-bold">Club Directory Submissions</h1>
                <p className="mb-4 text-sm text-default-500">Sports clubs submitted for the directory. Approve to publish.</p>

                {query.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : clubs.length === 0 ? (
                    <div className="py-12 text-center text-default-500">No submissions yet.</div>
                ) : (
                    <div className="grid gap-4">
                        {clubs.map((c) => (
                            <div key={c.uid} className="flex gap-4 rounded-xl border border-default-200 bg-content1 p-4">
                                {(c.image_url || c.logo_url) && <img src={c.image_url || c.logo_url} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" />}
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="truncate font-semibold">{c.name}</h3>
                                        {statusChip(c.status)}
                                    </div>
                                    <div className="mt-1 grid gap-0.5 text-sm">
                                        <div className="capitalize text-default-500">{[c.sport_types, [c.city, c.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || '—'}</div>
                                        {c.address && <div className="text-default-500">{c.address}{(c.lat && c.lng) ? ` (${c.lat}, ${c.lng})` : ''}</div>}
                                        {c.website && <a href={c.website} target="_blank" rel="noopener noreferrer" className="truncate text-primary">{c.website}</a>}
                                        {c.description && <div className="whitespace-pre-line text-default-600">{c.description}</div>}
                                        <div className="mt-1 text-default-500">
                                            {[c.phone, c.email].filter(Boolean).join(' · ')}
                                        </div>
                                        <div className="text-default-500">
                                            Submitted by {c.contact_name} — <a href={`mailto:${c.contact_email}`} className="text-primary">{c.contact_email}</a>
                                            {c.contact_phone ? ` · ${c.contact_phone}` : ''}
                                        </div>
                                    </div>
                                    <div className="mt-3 flex gap-2">
                                        <PButton size="sm" color="success" variant={c.status === 'approved' ? 'solid' : 'flat'}
                                                 isDisabled={c.status === 'approved' || setStatus.isPending}
                                                 startContent={<Icon icon="material-symbols-light:check" className="text-lg" />}
                                                 onPress={() => setStatus.mutate({uid: c.uid, status: 'approved'})}>Approve</PButton>
                                        <PButton size="sm" color="danger" variant={c.status === 'rejected' ? 'solid' : 'flat'}
                                                 isDisabled={c.status === 'rejected' || setStatus.isPending}
                                                 startContent={<Icon icon="material-symbols-light:close" className="text-lg" />}
                                                 onPress={() => setStatus.mutate({uid: c.uid, status: 'rejected'})}>Reject</PButton>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
